import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssembly } from '../context/AssemblyContext';
import { useAuth } from '../context/AuthContext';
import AssemblyDashboard from '../components/assembly/AssemblyDashboard';
import DelegationSummaryCard from '../components/DelegationSummaryCard';
import LoadingState from '../components/common/LoadingState';
import { useConfirm } from '../components/confirm/ConfirmDialogProvider';
import { Assembly, AssemblyScheduledOwnerView } from '../types/assembly';
import { assemblyService } from '../services/assemblyService';
import { getAssemblyTypeLabel, getAssemblyStatusLabel, getAssemblyModalityLabel, getAssemblyStatusBadgeClass } from '../utils/assemblyLabels';
import { formatAssemblyCalendarDateEs, formatAssemblyTimeEs } from '../utils/assemblyDate';

const OwnerDashboardPage: React.FC = () => {
  const { currentAssemblyId } = useAssembly();
  const { user, logout } = useAuth();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [upcomingAssemblies, setUpcomingAssemblies] = React.useState<AssemblyScheduledOwnerView[]>([]);
  const [isLoadingUpcoming, setIsLoadingUpcoming] = React.useState(false);
  const [expandedDelegationAssemblyId, setExpandedDelegationAssemblyId] = React.useState<string | null>(null);

  const handleLogout = async () => {
    const shouldLogout = await confirm({
      title: 'Cerrar sesión',
      message: '¿Seguro que deseas cerrar sesión?',
      confirmText: 'Cerrar sesión',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (!shouldLogout) return;

    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const handleAssemblySelect = (assembly: Assembly | null) => {
    // Los propietarios no pueden cambiar de asamblea, solo participan
    if (assembly) {
      window.location.reload();
    }
  };

  const formatAssemblyDate = (dateString: string) => {
    return formatAssemblyCalendarDateEs(dateString, dateString);
  };

  const formatAssemblyTime = (timeString: string) => {
    return formatAssemblyTimeEs(timeString, '--:--');
  };

  React.useEffect(() => {
    const loadUpcomingAssemblies = async () => {
      if (currentAssemblyId) return;

      setIsLoadingUpcoming(true);
      try {
        const assemblies = await assemblyService.getOwnerScheduledAssembliesView(50);
        setUpcomingAssemblies(assemblies || []);
      } catch (error) {
        console.error('Error cargando asambleas programadas:', error);
      } finally {
        setIsLoadingUpcoming(false);
      }
    };

    loadUpcomingAssemblies();
  }, [currentAssemblyId]);

  if (!currentAssemblyId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4 md:py-6">
              <div>
                <h1 className="text-xl md:text-3xl font-bold text-gray-900">
                  Sistema de Asambleas
                </h1>
              </div>
              {/* Desktop menu */}
              <div className="hidden md:flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {user?.firstName} {user?.lastName}
                </span>
                <button
                  onClick={() => navigate('/profile')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Mi Perfil
                </button>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Cerrar Sesión
                </button>
              </div>
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
            {/* Mobile menu */}
            {mobileMenuOpen && (
              <div className="md:hidden border-t border-gray-200 py-2">
                <div className="px-2 py-2 text-sm text-gray-600">
                  {user?.firstName} {user?.lastName}
                </div>
                <button
                  onClick={() => {
                    navigate('/profile');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Mi Perfil
                </button>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm font-medium text-red-600 hover:bg-gray-50"
                >
                  Cerrar Sesión
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="bg-white rounded-lg shadow-md p-4 md:p-6 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10m-11 9h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v11a2 2 0 002 2z"
                />
              </svg>
              <h2 className="mt-4 text-lg md:text-xl font-semibold text-gray-900">
                Vista previa de agenda
              </h2>
              <h3 className="mt-2 text-base md:text-lg font-medium text-gray-900">
                No hay asamblea activa en este momento
              </h3>
              <p className="mt-2 text-sm text-gray-600 max-w-3xl mx-auto">
                Puedes revisar las asambleas ya programadas. Cuando el administrador inicie una asamblea,
                ingresarás automáticamente al módulo de participación.
              </p>
              <div className="mt-4 inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700">
                Delegación disponible antes y durante la asamblea
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base md:text-lg font-semibold text-gray-900">Asambleas programadas</h3>
                <span className="text-sm text-gray-500">{upcomingAssemblies.length} en programación</span>
              </div>

              {isLoadingUpcoming ? (
                <LoadingState />
              ) : upcomingAssemblies.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
                  <svg
                    className="mx-auto h-10 w-10 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10m-11 9h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v11a2 2 0 002 2z"
                    />
                  </svg>
                  <h4 className="mt-3 text-base font-medium text-gray-900">No hay asambleas programadas por ahora</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Cuando el administrador cree una nueva asamblea, la verás aquí automáticamente.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {upcomingAssemblies.map((assembly) => (
                    <div
                      key={assembly.id}
                      className="rounded-lg border border-gray-200 bg-white p-4 md:p-5"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <h4 className="text-base font-semibold text-gray-900 leading-tight">{assembly.title}</h4>
                        <span className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getAssemblyStatusBadgeClass(assembly.status)}`}>
                          {getAssemblyStatusLabel(assembly.status)}
                        </span>
                      </div>

                      {assembly.description && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-2">{assembly.description}</p>
                      )}

                      <div className="mt-4 space-y-2 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">Fecha:</span>
                          <span className="capitalize">{formatAssemblyDate(assembly.scheduledDate)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">Hora:</span>
                          <span>{formatAssemblyTime(assembly.startTime)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">Modalidad:</span>
                          <span>{getAssemblyModalityLabel(assembly.isVirtual)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">Tipo:</span>
                          <span>{getAssemblyTypeLabel(assembly.type)}</span>
                        </div>
                        {assembly.isVirtual ? (
                          <div className="flex items-start gap-2">
                            <span className="font-medium text-gray-900">Enlace:</span>
                            {assembly.meetingLink ? (
                              <a
                                href={assembly.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline break-all"
                              >
                                Conectarse ahora
                              </a>
                            ) : (
                              <span className="text-amber-700">Pendiente por definir</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-start gap-2">
                            <span className="font-medium text-gray-900">Lugar:</span>
                            <span>{assembly.location || 'Pendiente por definir'}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">Quórum requerido:</span>
                          <span>{Math.round(assembly.quorumRequired)}%</span>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                        <p className="text-xs text-gray-500">
                          ¿No podrás asistir? Puedes delegar tu voto ahora mismo.
                        </p>
                        <button
                          onClick={() =>
                            setExpandedDelegationAssemblyId((prev) =>
                              prev === assembly.id ? null : assembly.id
                            )
                          }
                          className="inline-flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
                        >
                          {expandedDelegationAssemblyId === assembly.id ? 'Ocultar delegación' : 'Gestionar delegación'}
                        </button>
                      </div>

                      {expandedDelegationAssemblyId === assembly.id && (
                        <div className="mt-4">
                          <DelegationSummaryCard assemblyId={assembly.id} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 md:py-6">
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-gray-900">
                Sistema de Asambleas
              </h1>
              <p className="text-xs md:text-sm text-gray-600 mt-1">Participación en Asamblea</p>
            </div>
            {/* Desktop menu */}
            <div className="hidden md:flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {user?.firstName} {user?.lastName}
              </span>
              <button
                onClick={() => navigate('/profile')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Mi Perfil
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Cerrar Sesión
              </button>
            </div>
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-2">
              <div className="px-2 py-2 text-sm text-gray-600">
                {user?.firstName} {user?.lastName}
              </div>
              <button
                onClick={() => {
                  navigate('/profile');
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Mi Perfil
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm font-medium text-red-600 hover:bg-gray-50"
              >
                Cerrar Sesión
              </button>
            </div>
          )}
        </div>
      </div>

      <AssemblyDashboard
        assemblyId={currentAssemblyId}
        userRole="owner"
        onAssemblySelect={handleAssemblySelect}
      />
    </div>
  );
};

export default OwnerDashboardPage;
